import Navigation from "@/components/Navigation";
import { Card, CardContent } from "@/components/ui/card";
import heroAbout from "@/assets/hero-about.webp";
import PrimaryCTA from "@/components/PrimaryCTA";
import Footer from "@/components/Footer";
import simeonPortrait from "@/assets/simeon.webp"; // ensure file exists
import useInView from "@/hooks/useInView"; // create this hook

const About = () => {
  const { ref, isVisible } = useInView();

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section
        className="relative w-full px-4 py-20 md:py-32 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.25)), url(${heroAbout})`,
          backgroundSize: "cover",
          backgroundPosition: "top center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white">
            About Us
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8">
            Expert IT consultancy rooted in Nordic values
          </p>
          <p className="text-lg text-white/80 mb-10">
            At TechG8, IT is more than infrastructure, it’s craftsmanship.  
            We shape technology into solutions that last.
          </p>
          <PrimaryCTA />
        </div>
      </section>

      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">

          {/* Company Story */}
          <Card className="mb-16">
            <CardContent className="pt-6 space-y-6">
              <p className="text-lg text-muted-foreground">
                At TechG8, we believe IT should empower your business, not hold it back.
              </p>
              <p className="text-lg text-muted-foreground">
                Our founder has spent years delivering complex IT projects across government,
                finance, and enterprise organizations. From leading cloud migrations to securing
                large scale infrastructures, he has worked hands‑on with environments where
                reliability, compliance and security are non‑negotiable.
              </p>
              <p className="text-lg text-muted-foreground">
                This experience inspired the creation of TechG8, a consultancy built to bring
                enterprise‑grade expertise to businesses of all sizes.
              </p>
              <h3 className="text-xl font-semibold">Our mission is simple:</h3>
              <ul className="list-disc list-inside space-y-2 text-lg text-muted-foreground">
                <li>Make digital transformation easy and secure</li>
                <li>Deliver solutions that are scalable and cost effective</li>
                <li>Free you to focus on what matters most to grow your business</li>
              </ul>
              <p className="text-lg text-muted-foreground">
                When you work with us, you don’t get a faceless team. You work directly with an
                expert who understands both the technical challenges and the business outcomes you
                need.
              </p>
            </CardContent>
          </Card>

          {/* Our Approach */}
          <section className="mb-16 bg-muted rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Our Approach</h2>
            <p className="text-lg text-muted-foreground mb-4">
              We combine Nordic values with enterprise grade expertise to deliver IT solutions that
              are secure, scalable, and aligned with your business goals.
            </p>
            <ul className="list-disc list-inside space-y-2 text-lg text-muted-foreground">
              <li><strong>Strategy First:</strong> We start with your objectives, not the technology.</li>
              <li><strong>Security by Design:</strong> Compliance and protection are built into every solution.</li>
              <li><strong>Cloud Native:</strong> We leverage modern platforms for agility and cost efficiency.</li>
              <li><strong>Human Centric:</strong> We ensure technology empowers people, not the other way around.</li>
            </ul>
          </section>

          {/* Mission */}
          <Card className="mb-16">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
              <p className="text-lg text-muted-foreground">
                To simplify digital transformation by providing reliable, scalable and secure IT
                solutions that enable organizations to grow with confidence and agility.
              </p>
            </CardContent>
          </Card>

          {/* Vision */}
          <section className="mb-16 bg-muted rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Our Vision</h2>
            <p className="text-lg text-muted-foreground">
              To be a trusted technology partner for businesses worldwide, recognized for
              innovation, quality and customer centric service.
            </p>
          </section>

          {/* Core Values */}
          <Card className="mb-16">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold mb-4">Core Values</h2>
              <ul className="list-disc list-inside space-y-2 text-lg text-muted-foreground">
                <li><strong>Customer Focus:</strong> We listen, understand and prioritize your unique needs.</li>
                <li><strong>Integrity:</strong> We uphold transparency and ethical practices in every engagement.</li>
                <li><strong>Innovation:</strong> We embrace emerging technologies to drive continuous improvement.</li>
                <li><strong>Excellence:</strong> We deliver high quality solutions that exceed expectations.</li>
                <li><strong>Collaboration:</strong> We work closely with clients and partners to achieve shared success.</li>
              </ul>
            </CardContent>
          </Card>

          {/* CTA */}
          <section className="text-center py-12 bg-muted rounded-lg mb-16">
            <h2 className="text-3xl font-bold mb-4">Let's Work Together</h2>
            <p className="text-muted-foreground mb-6">
              Ready to discuss your IT challenges? Book a free consultation today.
            </p>
            <PrimaryCTA />
          </section>
        </div>
      </main>

      {/* Founder Section - Full Width, Scroll-triggered animation */}
      <section
        ref={ref}
        className="w-full flex flex-col md:flex-row items-center gap-12 py-24 overflow-hidden bg-background px-4 md:px-16"
      >
        {/* Image */}
        <div
          className={`w-full md:w-1/2 transition-all duration-1000 ease-out ${
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
          }`}
        >
          <img
            src={simeonPortrait}
            alt="Simeon Dimitrov"
            className="rounded-lg shadow-md object-cover w-full h-auto"
          />
        </div>

        {/* Text */}
        <div
          className={`w-full md:w-1/2 space-y-6 transition-all duration-1000 ease-out ${
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold leading-tight">
            It all started<br />with Simeon
          </h2>
          <p className="text-base text-muted-foreground">
            With years of experience delivering complex IT projects across government,
            finance, and enterprise organizations, Simeon saw how businesses struggled
            to balance innovation with reliability.
          </p>
          <p className="text-base text-muted-foreground">
            His journey from designing secure cloud infrastructures to leading digital
            transformation initiatives inspired the creation of TechG8. Today, he brings
            enterprise grade expertise to businesses of all sizes, ensuring technology
            empowers growth rather than holding it back.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
